import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  CBadge, CCard, CCardBody, CCardHeader, CCol, CRow, CForm, CFormInput, CInputGroup, CInputGroupText, CFormLabel, CFormSelect, CButton, CAlert,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter, CTable, CTableHead,
  CTableRow, CTableHeaderCell, CTableBody, CTableDataCell, CSpinner,
} from '@coreui/react'
import { api, currentUser } from '../api/client'
import { hoyLocal } from '../utils/fecha'
import { capitalizarPalabras } from '../utils/texto'
import { fmtMoneda, simboloMoneda } from '../utils/moneda'
import { NumericFormat } from 'react-number-format'
import WwGrid, { WwColumn } from '../components/ww/WwGrid'
import WwFilterBar from '../components/ww/WwFilterBar'
import { usePendiente } from '../components/ww/pendiente'
import { estadoEmpresa } from '../utils/estadoEmpresa'

const TIPOS_PAGO: Array<[number, string]> = [
  [1, 'Registro'], [2, 'Mantenimiento'], [3, 'Impresora - WhatsApp'], [4, 'Asesoría Personalizada'],
  // [RGM] Módulo Facturación (PagoTipo.ModuloFacturacion = 5).
  [5, 'Módulo Facturación'],
  // [RGM] 2026-09-29 (pedido Rafael): 6 = solo Tickets de impresión.
  [6, 'Tickets de Impresión'],
]
const ENTIDADES_PAGO: Array<[number, string]> = [
  [0, 'Dónde Realizó el Pago?'], [1, 'Western Union'], [2, 'Money Gram'], [3, 'Vigo'],
  [4, 'Afex'], [5, 'Bancolombia'], [6, 'Giros y Finanzas'], [7, 'Gane - Super Giros'],
  [8, 'Efecty'], [9, 'Cybertronica'], [10, 'PayU'], [11, 'PayPal'], [12, 'Online'], [13, 'Wompi'],
]
// [RGM] 2026-09-17 (reporte Rafael: "en pagos… estado, entidad y tipo aún sale
// en números en grilla"): etiquetas también en la GRILLA (los arrays de arriba
// alimentan el form; estos mapas alimentan las columnas).
const TIPO_TXT: Record<number, string> = Object.fromEntries(TIPOS_PAGO)
const ENTIDAD_TXT: Record<number, string> = Object.fromEntries(ENTIDADES_PAGO)

// [RGM] 2026-10-09 (pedido Rafael — caso MAIN #19/#20: la misma referencia
// bancaria subida 2 veces): clave de agrupación anti-duplicado de un pago
// PENDIENTE. Con referencia: la referencia (trim). Sin referencia: valor +
// fecha de pago como tentativo (mismo criterio del aviso del SubirPagoModal y
// del historial de CuentaPage). Esta grilla es GLOBAL (superadmin): la
// comparación se agrupa por EMPRESA en el llamador.
const claveDuplicadoPago = (p: any): string => {
  const referencia = String(p.pagoRefrencia ?? '').trim()
  return referencia !== ''
    ? `REF:${referencia}`
    : `VF:${Number(p.pagoValor ?? 0)}:${String(p.pagoFechaPago ?? '').slice(0, 10)}`
}

const columnas: WwColumn[] = [
  // [RGM] 2026-10-02 (pedido Rafael): el Nro es la PRIMERA columna.
  { key: 'pagoNro', title: 'Nro', type: 'number' },
  { key: 'empresaCodigo', title: 'Empresa Codigo' },
  { key: 'empresaNombre', title: 'Empresa Nombre' },
  { key: 'pagoFecha', title: 'Fecha', type: 'date' },
  { key: 'pagoHora', title: 'Hora' },
  { key: 'empresaFechaCreacion', title: 'Empresa Fecha Creacion', type: 'date' },
  { key: 'empresaRepreLegal', title: 'Empresa Repre Legal' },
  {
    // [RGM] 2026-09-17 (reporte Rafael "estado… aún sale en números"): badge GX
    // de la empresa (mismo estadoEmpresa de la grilla de Empresas).
    key: 'empresaEstado', title: 'Empresa Estado',
    render: (row: any) => {
      const e = estadoEmpresa(Number(row.empresaEstado ?? 0))
      return <CBadge color={e.color}>{e.label}</CBadge>
    },
    // [RGM] 2026-10-03 (pedido Rafael: el .xlsx debe mostrar el TEXTO visible):
    // exportValue espeja el render (mismo estadoEmpresa().label).
    exportValue: (row: any) => estadoEmpresa(Number(row.empresaEstado ?? 0)).label,
  },
  { key: 'empresaTelefono', title: 'Empresa Telefono' },
  { key: 'empresaSaldoActual', title: 'Otros Saldos Pendientes', type: 'currency' },
  { key: 'empresaTotalPagos', title: 'Empresa Total Pagos', type: 'currency' },
  { key: 'empresaUltimoPago', title: 'Empresa Ultimo Pago', type: 'date' },
  { key: 'empresaProximoCobro', title: 'Empresa Proximo Cobro', type: 'date' },
  { key: 'pagoValor', title: 'Valor', type: 'currency' },
  { key: 'pagoProximoValor', title: 'Proximo Valor', type: 'currency' },
  { key: 'pagoProximoCobro', title: 'Proximo Cobro', type: 'date' },
  { key: 'pagoObservacion', title: 'Observacion' },
  { key: 'pagoFechaPago', title: 'Fecha Pago', type: 'date' },
  // [RGM] 2026-09-17 (reporte Rafael): entidad en TEXTO (combo GX).
  // [RGM] 2026-10-03 (pedido Rafael: el .xlsx debe mostrar el TEXTO visible):
  // exportValue espeja el render (mismo mapa y fallback).
  {
    key: 'pagoEntidad', title: 'Entidad',
    render: (row: any) => ENTIDAD_TXT[Number(row.pagoEntidad)] ?? '—',
    exportValue: (row: any) => ENTIDAD_TXT[Number(row.pagoEntidad)] ?? '—',
  },
  { key: 'pagoNombreRemitente', title: 'Nombre Remitente' },
  // [RGM] 2026-09-16 (reporte Rafael "aun me aparece con numeros"): el combo
  // GX (pago.cs:3973-3974) es 1=Si / 2=No — con el formateo 'bool' genérico
  // el 2 (No) se mostraba como "Sí" (ambos son truthy). Render explícito.
  // [RGM] 2026-10-03 (pedido Rafael: el .xlsx debe mostrar el TEXTO visible):
  // exportValue espeja el render (mismo 1=Si / 2=No / otro vacío).
  {
    key: 'pagoConfirmado', title: 'Confirmado',
    render: (row: any) => (
      <>
        {Number(row.pagoConfirmado) === 1 ? 'Si' : Number(row.pagoConfirmado) === 2 ? 'No' : ''}
        {/* [RGM] 2026-10-09 (pedido Rafael — caso MAIN #19/#20): fila
            PENDIENTE que comparte referencia con OTRA pendiente de la MISMA
            empresa → posible duplicado, ANTES de confirmar. */}
        {row.posibleDuplicado === true && (
          <span className="badge text-warning border border-warning ms-1">Posible duplicado</span>
        )}
      </>
    ),
    // El .xlsx conserva el TEXTO del estado (el badge es señal visual).
    exportValue: (row: any) => (Number(row.pagoConfirmado) === 1 ? 'Si' : Number(row.pagoConfirmado) === 2 ? 'No' : ''),
  },
  { key: 'pagoCiudad', title: 'Ciudad' },
  { key: 'pagoPais', title: 'Pais' },
  // [RGM] 2026-09-17 (reporte Rafael): tipo en TEXTO (combo GX).
  // [RGM] 2026-10-03 (pedido Rafael: el .xlsx debe mostrar el TEXTO visible):
  // exportValue espeja el render (mismo mapa y fallback).
  {
    key: 'pagoTipo', title: 'Tipo',
    render: (row: any) => TIPO_TXT[Number(row.pagoTipo)] ?? '—',
    exportValue: (row: any) => TIPO_TXT[Number(row.pagoTipo)] ?? '—',
  },
  // [RGM] 2026-09-16 (pedido Rafael): mostrar la imagen del pago (comprobante).
  {
    key: 'comprobanteUrl', title: 'Comprobante', width: 120,
    render: (row: any) => row.comprobanteUrl
      ? <a href={row.comprobanteUrl} target="_blank" rel="noreferrer">Ver</a>
      : '—',
  },
]

// Pagos — réplica de pagogeneral.cs:53 (pagogeneral GX).
// Prop superadmin: el superadmin ve pagos de TODAS las empresas (grid global
// /superadmin/pagos) y el campo Empresa del form queda editable en "Nuevo"
// (la empresa por fila se usa para Editar/Eliminar). Sin resumen
// total-a-pagar (es por empresa, no global — no se inventa).
const PagosPage: React.FC<{ superadmin?: boolean }> = ({ superadmin }) => {
  const user = currentUser()
  const empresa = superadmin ? '' : (user?.companyId ?? '01')
  const [rows, setRows] = useState<any[]>([])
  const formRef = useRef<HTMLFormElement>(null)
  // ── Filtros rápidos: empresa (solo superadmin global) y confirmación ──
  const [filtroConfirmado, setFiltroConfirmado] = useState('')
  const [filtroEmpresa, setFiltroEmpresa] = useState('')
  // [RGM] 2026-10-02 (pedido Rafael): grilla ÚNICA de superadmin con botones
  // de categoría estilo Préstamos — mismas 3 secciones de antes, ahora como
  // filtro de navegación.
  const [categoriaSuper, setCategoriaSuper] = useState<'sin-confirmar' | 'confirmados' | 'cuentas'>('sin-confirmar')
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [abierto, setAbierto] = useState(false)
  const { aviso, setAviso } = usePendiente()
  const [resumen, setResumen] = useState<any>(null)
  // [RGM] 2026-09-29 (B2): cuentas de cobro pendientes para el desglose.
  const [cuentasCobro, setCuentasCobro] = useState<any>(null)
  const [confirmacion, setConfirmacion] = useState<any>(null)
  const [guardando, setGuardando] = useState(false)
  const [errorModal, setErrorModal] = useState('')
  // Fila en edición (pagoww.cs:3360 'DO UPDATE' → webobject pago Mode="UPD")
  const [editando, setEditando] = useState<any | null>(null)
  const [form, setForm] = useState({
    empresaCodigo: empresa, valor: '',
    fecha: hoyLocal(),
    proximoCobro: hoyLocal(),
    observacion: '', entidad: '', remitente: '',
    pagoProximoValor: '', pagoFechaPago: '', pagoRefrencia: '',
    pagoConfirmado: '', pagoCiudad: '', pagoPais: '', pagoTipo: '',
    pagoLibreValor1: '', pagoLibreValor2: '', pagoLibreValor3: '',
    pagoLibreNumerico1: '', pagoLibreNumerico2: '', pagoLibreNumerico3: '',
    pagoLibreCaracter1: '', pagoLibreCaracter2: '', pagoLibreCaracter3: '',
  })

  const formVacio = () => ({
    empresaCodigo: superadmin ? '' : empresa, valor: '',
    fecha: hoyLocal(),
    proximoCobro: hoyLocal(),
    observacion: '', entidad: '', remitente: '',
    pagoProximoValor: '', pagoFechaPago: '', pagoRefrencia: '',
    pagoConfirmado: '', pagoCiudad: '', pagoPais: '', pagoTipo: '',
    pagoLibreValor1: '', pagoLibreValor2: '', pagoLibreValor3: '',
    pagoLibreNumerico1: '', pagoLibreNumerico2: '', pagoLibreNumerico3: '',
    pagoLibreCaracter1: '', pagoLibreCaracter2: '', pagoLibreCaracter3: '',
  })

  useEffect(() => {
    setCargando(true)
    if (superadmin) {
      // Grid GLOBAL de pagos (todas las empresas) — SuperAdminController
      api.get<any[]>('/superadmin/pagos')
        .then(setRows)
        .catch((e: any) => setError(e.message))
        .finally(() => setCargando(false))
      return
    }
    Promise.all([
      api.get<any[]>(`/pagos?empresa=${empresa}`),
      api.get<any>(`/pagos/total-a-pagar?empresa=${empresa}`),
      api.get<any>(`/pagos/confirmar?empresa=${empresa}`),
      api.get<any>(`/pagos/cuentas?empresa=${empresa}`),
    ])
      .then(([p, tot, conf, cc]) => {
        setRows(p)
        setResumen(tot)
        setConfirmacion(conf)
        setCuentasCobro(cc)
      })
      .catch((e: any) => setError(e.message))
      .finally(() => setCargando(false))
  }, [empresa, superadmin])

  // Filas filtradas por confirmación (client-side, patrón Préstamos)
  const visibleRows = useMemo(() => rows.filter((row) => {
    if (filtroEmpresa && String(row.empresaCodigo ?? '').trim() !== filtroEmpresa) return false
    if (filtroConfirmado) {
      // [RGM] 2026-09-16: 1=Si / 2=No (combo GX pago.cs:3973-3974).
      const conf = Number(row.pagoConfirmado ?? 0) === 1 ? 'Si' : 'No'
      if (conf !== filtroConfirmado) return false
    }
    return true
  }), [rows, filtroEmpresa, filtroConfirmado])

  // [RGM] 2026-09-29 (pedido Rafael): la grilla global de superadmin se divide
  // en TRES secciones (Variante A): (1) Por confirmar = comprobante subido
  // (estado 2); (2) Cuentas de cobro = generadas SIN comprobante (0 — no le
  // salen al cliente) y anuladas (3); (3) Confirmados (1).
  const esPagoCuenta = (r: any) =>
    String(r.pagoObservacion ?? '').trim().startsWith('Cuenta de cobro')
  const porConfirmar = visibleRows.filter((r) => Number(r.pagoConfirmado ?? 0) === 2)
  const cuentasCobroRows = visibleRows.filter((r) => esPagoCuenta(r)
    && (Number(r.pagoConfirmado ?? 0) === 0 || Number(r.pagoConfirmado ?? 0) === 3))
  const confirmadosRows = visibleRows.filter((r) => Number(r.pagoConfirmado ?? 0) === 1)

  // [RGM] 2026-10-09 (pedido Rafael — anti-duplicado de pagos): claves
  // (EMPRESA + referencia o valor+fecha) de las filas PENDIENTES
  // (PagoConfirmado = 2) que comparten referencia con OTRA pendiente de la
  // MISMA empresa → badge "Posible duplicado" en la grilla, ANTES de
  // confirmar. Las confirmadas (1) NUNCA se marcan.
  const clavesDuplicadas = useMemo(() => {
    const porClave = new Map<string, number>()
    for (const r of rows) {
      if (Number(r.pagoConfirmado) !== 2) continue
      const clave = `${String(r.empresaCodigo ?? '').trim()}|${claveDuplicadoPago(r)}`
      porClave.set(clave, (porClave.get(clave) ?? 0) + 1)
    }
    return new Set(
      [...porClave.entries()].filter(([, veces]) => veces > 1).map(([clave]) => clave),
    )
  }, [rows])

  // Fila con el flag para la columna Confirmado (solo pendientes marcadas).
  const conFlagDuplicado = (r: any) =>
    Number(r.pagoConfirmado) === 2
      && clavesDuplicadas.has(`${String(r.empresaCodigo ?? '').trim()}|${claveDuplicadoPago(r)}`)
      ? { ...r, posibleDuplicado: true }
      : r

  const empresasPago = useMemo(
    () => [...new Set(rows.map((r) => String(r.empresaCodigo ?? '').trim()).filter(Boolean))],
    [rows],
  )
  const chips = [
    filtroEmpresa ? { clave: 'empresa', label: `Empresa: ${filtroEmpresa}` } : null,
    filtroConfirmado ? { clave: 'conf', label: `Confirmado: ${filtroConfirmado}` } : null,
  ].filter(Boolean) as { clave: string; label: string }[]
  const limpiarFiltros = () => { setFiltroEmpresa(''); setFiltroConfirmado('') }
  const quitarFiltro = (clave: string) => {
    if (clave === 'empresa') setFiltroEmpresa('')
    if (clave === 'conf') setFiltroConfirmado('')
  }

  const abrirNuevo = () => {
    setEditando(null)
    setForm(formVacio())
    setAbierto(true)
  }

  // Modificar (pagoww.cs:3360): carga la fila en el form y abre el modal.
  // PagoNro/Fecha/Hora no son editables (pago.cs:1790-1795, 3793-3797) y
  // EmpresaCodigo está deshabilitado en UPD (pago.cs:1829-1833).
  const abrirEditar = (fila: any) => {
    setErrorModal('')
    setEditando(fila)
    setForm({
      empresaCodigo: fila.empresaCodigo ?? empresa,
      valor: String(fila.pagoValor ?? ''),
      fecha: String(fila.pagoFecha ?? '').slice(0, 10),
      proximoCobro: String(fila.pagoProximoCobro ?? hoyLocal()).slice(0, 10),
      observacion: (fila.pagoObservacion ?? '').trim(),
      entidad: String(fila.pagoEntidad ?? 0),
      remitente: fila.pagoNombreRemitente ?? '',
      pagoProximoValor: fila.pagoProximoValor != null ? String(fila.pagoProximoValor) : '',
      pagoFechaPago: fila.pagoFechaPago ? String(fila.pagoFechaPago).slice(0, 10) : '',
      pagoRefrencia: fila.pagoRefrencia ?? '',
      pagoConfirmado: fila.pagoConfirmado != null ? String(fila.pagoConfirmado) : '',
      pagoCiudad: fila.pagoCiudad ?? '',
      pagoPais: fila.pagoPais ?? '',
      pagoTipo: fila.pagoTipo != null ? String(fila.pagoTipo) : '',
      pagoLibreValor1: fila.pagoLibreValor1 != null ? String(fila.pagoLibreValor1) : '',
      pagoLibreValor2: fila.pagoLibreValor2 != null ? String(fila.pagoLibreValor2) : '',
      pagoLibreValor3: fila.pagoLibreValor3 != null ? String(fila.pagoLibreValor3) : '',
      pagoLibreNumerico1: fila.pagoLibreNumerico1 != null ? String(fila.pagoLibreNumerico1) : '',
      pagoLibreNumerico2: fila.pagoLibreNumerico2 != null ? String(fila.pagoLibreNumerico2) : '',
      pagoLibreNumerico3: fila.pagoLibreNumerico3 != null ? String(fila.pagoLibreNumerico3) : '',
      pagoLibreCaracter1: fila.pagoLibreCaracter1 ?? '',
      pagoLibreCaracter2: fila.pagoLibreCaracter2 ?? '',
      pagoLibreCaracter3: fila.pagoLibreCaracter3 ?? '',
    })
    setAbierto(true)
  }

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorModal('')
    setGuardando(true)
    try {
      const body = {
        empresaCodigo: form.empresaCodigo,
        valor: Number(form.valor),
        proximoCobro: form.proximoCobro,
        observacion: form.observacion || undefined,
        entidad: Number(form.entidad) || 0,
        remitente: form.remitente || undefined,
        pagoProximoValor: form.pagoProximoValor !== '' ? Number(form.pagoProximoValor) : null,
        pagoFechaPago: form.pagoFechaPago || null,
        pagoRefrencia: form.pagoRefrencia || null,
        pagoConfirmado: form.pagoConfirmado !== '' ? Number(form.pagoConfirmado) : null,
        pagoCiudad: form.pagoCiudad || null,
        pagoPais: form.pagoPais || null,
        pagoTipo: form.pagoTipo !== '' ? Number(form.pagoTipo) : null,
        pagoLibreValor1: form.pagoLibreValor1 !== '' ? Number(form.pagoLibreValor1) : null,
        pagoLibreValor2: form.pagoLibreValor2 !== '' ? Number(form.pagoLibreValor2) : null,
        pagoLibreValor3: form.pagoLibreValor3 !== '' ? Number(form.pagoLibreValor3) : null,
        pagoLibreNumerico1: form.pagoLibreNumerico1 !== '' ? Number(form.pagoLibreNumerico1) : null,
        pagoLibreNumerico2: form.pagoLibreNumerico2 !== '' ? Number(form.pagoLibreNumerico2) : null,
        pagoLibreNumerico3: form.pagoLibreNumerico3 !== '' ? Number(form.pagoLibreNumerico3) : null,
        pagoLibreCaracter1: form.pagoLibreCaracter1 || null,
        pagoLibreCaracter2: form.pagoLibreCaracter2 || null,
        pagoLibreCaracter3: form.pagoLibreCaracter3 || null,
      }
      if (editando) {
        // pagoww.cs:3360 — DO UPDATE: webobject pago Mode="UPD" con PagoNro
        // (la llave viaja en la URL). El backend replica BC000K10
        // (pago_bc.cs:2388) + UpdateTablesN10K17 (pago_bc.cs:2390).
        await api.put(`/pagos/${editando.pagoNro}`, body)
      } else {
        await api.post('/pagos', { ...body, fecha: form.fecha })
      }
      setAbierto(false)
      setEditando(null)
      setCargando(true)
      api.get<any[]>(superadmin ? '/superadmin/pagos' : `/pagos?empresa=${empresa}`)
        .then(setRows).finally(() => setCargando(false))
    } catch (err: any) {
      setErrorModal(err.message)
    } finally {
      setGuardando(false)
    }
  }

  const eliminar = (fila: any) => {
    // pagoww.cs:3376 — DO DELETE: webobject pago Mode="DLT" con PagoNro.
    // El backend replica BC000K11 (pago_bc.cs:2389) y revierte
    // EmpresaTotalPagos (pago_bc.cs:769) — sin control de referencias.
    if (!window.confirm(`¿Eliminar el pago ${fila.pagoNro}?`)) return
    api.delete(`/pagos/${fila.pagoNro}?empresa=${fila.empresaCodigo}`)
      .then(() => {
        setAviso('Pago eliminado.')
        setCargando(true)
        api.get<any[]>(superadmin ? '/superadmin/pagos' : `/pagos?empresa=${empresa}`)
          .then(setRows).finally(() => setCargando(false))
      })
      .catch((e: any) => setError(e.message))
  }

  const [totalReg, setTotalReg] = useState(rows.length)
  return (
    <>
      <CRow className="mb-4">
        <CCol>
          <h4>{superadmin ? 'Pagos' : 'Pago'}</h4>
          <small className="text-body-secondary">{totalReg} {totalReg === 1 ? 'registro' : 'registros'}</small>
        </CCol>
      </CRow>
      {error && <CAlert color="danger">{error}</CAlert>}
      {aviso && <CAlert color="warning" dismissible onClose={() => setAviso('')}>{aviso}</CAlert>}

      {!superadmin && resumen && (
        <CCard className="mb-4">
          <CCardBody>
            {/* [RGM] 2026-09-29 (pedido Rafael): empresa en PRUEBA (6) no
                muestra prorrateo — no tiene cobro. */}
            {Number(rows[0]?.empresaEstado ?? 0) === 6 ? (
              <div className="fw-semibold">En prueba (sin cobro)</div>
            ) : (
            <CTable hover responsive striped>
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell>Saldo Total</CTableHeaderCell>
                  <CTableHeaderCell>Cuentas de cobro pendientes</CTableHeaderCell>
                  <CTableHeaderCell>Otros saldos pendientes</CTableHeaderCell>
                  <CTableHeaderCell>Tarifa mensual</CTableHeaderCell>
                  <CTableHeaderCell>Pago confirmado</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                <CTableRow>
                  <CTableDataCell>
                    {fmtMoneda(Number(cuentasCobro?.totalPendiente ?? 0) + Number(resumen.otrosSaldosPendientes ?? 0))}
                  </CTableDataCell>
                  <CTableDataCell>
                    {/* [RGM] 2026-09-29 (B2 aprobado): el total por CUENTAS
                        reales (suma de las pendientes); respaldo GX (meses
                        vencidos) solo si no hay cuentas generadas. */}
                    {/* [RGM] 2026-09-29: sin guion (parecía resta) — paréntesis. */}
                    {(cuentasCobro?.cuentas?.length ?? 0) > 0
                      ? `${cuentasCobro?.cuentas?.length} (${fmtMoneda(cuentasCobro?.totalPendiente)})`
                      : `Meses vencidos: ${resumen.mesesVencidos ?? 0}`}
                  </CTableDataCell>
                  <CTableDataCell>
                    {fmtMoneda(resumen.otrosSaldosPendientes)}
                    {String(resumen.motivoSaldo ?? '').trim() && (
                      <div className="small text-body-secondary">{String(resumen.motivoSaldo).trim()}</div>
                    )}
                  </CTableDataCell>
                  <CTableDataCell>
                    {fmtMoneda(resumen.tarifaMensual)}
                    {/* [RGM] 2026-09-29 (pedido Rafael): sin la palabra "Base" —
                        la tarifa se describe con sus sumandos. */}
                    <div className="small text-body-secondary">
                      ({fmtMoneda(resumen.tarifaBase)} de mantenimiento
                      {Number(resumen.tarifaFotos ?? 0) !== 0 && (
                        <> + {fmtMoneda(resumen.tarifaFotos)} de fotos</>
                      )}
                      {Number(resumen.tarifaTronia ?? 0) !== 0 && (
                        <> + {fmtMoneda(resumen.tarifaTronia)} de TronIA</>
                      )}
                      {Number(resumen.tarifaAjuste ?? 0) !== 0 && (
                        <> + {fmtMoneda(resumen.tarifaAjuste)} de ajuste</>
                      )})
                    </div>
                  </CTableDataCell>
                  <CTableDataCell>{confirmacion?.confirmado ? 'Sí' : 'No'}</CTableDataCell>
                </CTableRow>
              </CTableBody>
            </CTable>
            )}
          </CCardBody>
        </CCard>
      )}

      {cargando && <div className="text-center py-4"><CSpinner color="primary" /></div>}

      {superadmin ? (
        <>
          {/* [RGM] 2026-10-02 (pedido Rafael): UNA sola grilla con botones de
              categoría estilo Préstamos (las 3 categorías son disjuntas:
              2=por confirmar / cuentas de cobro 0-3 / 1=confirmados). */}
          <WwGrid
            filters={
              <div className="d-flex flex-column gap-2">
                <div className="d-flex gap-1 flex-wrap">
                  {([
                    { key: 'sin-confirmar', label: `Sin Confirmar (${porConfirmar.length})` },
                    { key: 'confirmados', label: `Confirmados (${confirmadosRows.length})` },
                    { key: 'cuentas', label: `Cuentas de Cobro (${cuentasCobroRows.length})` },
                  ] as const).map((c) => (
                    <CButton
                      key={c.key}
                      size="sm"
                      color={categoriaSuper === c.key ? 'primary' : 'outline-primary'}
                      onClick={() => setCategoriaSuper(c.key)}
                    >
                      {c.label}
                    </CButton>
                  ))}
                </div>
                <WwFilterBar
                  selects={[
                    { key: 'empresa', label: 'Todas las Empresas', opciones: empresasPago, valor: filtroEmpresa, onChange: setFiltroEmpresa },
                  ]}
                  chips={chips}
                  onQuitar={quitarFiltro}
                  onLimpiar={limpiarFiltros}
                />
              </div>
            }
            title="Pagos"
            columns={columnas}
            rows={(categoriaSuper === 'confirmados' ? confirmadosRows
              : categoriaSuper === 'cuentas' ? cuentasCobroRows
              : porConfirmar).map(conFlagDuplicado)}
            onTotalChange={setTotalReg}
            onAgregar={abrirNuevo}
            onEditar={abrirEditar}
            onEliminar={eliminar}
            busquedaPlaceholder="Buscar por pago, empresa o remitente..."
          />
        </>
      ) : (
        <WwGrid
          filters={
            <WwFilterBar
              selects={[
                ...(superadmin ? [{ key: 'empresa', label: 'Todas las Empresas', opciones: empresasPago, valor: filtroEmpresa, onChange: setFiltroEmpresa }] : []),
                { key: 'conf', label: 'Todos los Estados', opciones: ['Si', 'No'], valor: filtroConfirmado, onChange: setFiltroConfirmado },
              ]}
              chips={chips}
              onQuitar={quitarFiltro}
              onLimpiar={limpiarFiltros}
            />
          }
          title="Pago"
          columns={columnas}
          rows={visibleRows.map(conFlagDuplicado)}
          onTotalChange={setTotalReg}
          onAgregar={abrirNuevo}
          onEditar={abrirEditar}
          onEliminar={eliminar}
          busquedaPlaceholder="Buscar por pago, empresa o remitente..."
        />
      )}

      <CModal visible={abierto} onClose={() => { setAbierto(false); setEditando(null) }} size="lg">
        <CModalHeader>
          <CModalTitle>
            {/* [RGM] 2026-09-29 (reporte Rafael): identificar la EMPRESA del
                pago — solo el Nro no es claro. */}
            {editando
              ? `Modificar pago ${editando.pagoNro} — ${String(editando.empresaNombre ?? '').trim() || 'Empresa'} (${String(editando.empresaCodigo ?? '').trim()})`
              : 'Registrar pago'}
          </CModalTitle>
        </CModalHeader>
        <CModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {errorModal && <CAlert color="danger">{errorModal}</CAlert>}
          <CForm ref={formRef} onSubmit={guardar}>
            {/* [RGM] 2026-09-16 (pedido Rafael): el form del pago organizado en
                TARJETAS estilo SiprinIA; al editar se muestra la IMAGEN del
                comprobante. */}
            <CCard className="mb-3">
              <CCardHeader className="fw-semibold">Pago</CCardHeader>
              <CCardBody>
                <CRow className="g-3">
                  {editando && (
                    <CCol md={4}><CFormLabel>Nro</CFormLabel><CFormInput value={editando.pagoNro} disabled /></CCol>
                  )}
                  <CCol md={4}><CFormLabel>Empresa</CFormLabel><CFormInput value={form.empresaCodigo} disabled={editando !== null} onChange={(e) => setForm({ ...form, empresaCodigo: e.target.value })} /></CCol>
                  {/* [RGM] 2026-10-02 (moneda global, pedido Rafael): prefijo
                      de la moneda configurada + formato de miles en vivo. */}
                  <CCol md={4}>
                    <CFormLabel>Valor</CFormLabel>
                    <CInputGroup>
                      <CInputGroupText>{simboloMoneda()}</CInputGroupText>
                      <NumericFormat
                        className="form-control"
                        value={form.valor}
                        onValueChange={(v) => setForm({ ...form, valor: v.value })}
                        thousandSeparator="," decimalSeparator="." decimalScale={2} fixedDecimalScale
                        allowNegative={false} inputMode="decimal"
                      />
                    </CInputGroup>
                  </CCol>
                  <CCol md={4}>
                    <CFormLabel>Próximo Valor</CFormLabel>
                    <CInputGroup>
                      <CInputGroupText>{simboloMoneda()}</CInputGroupText>
                      <NumericFormat
                        className="form-control"
                        value={form.pagoProximoValor}
                        onValueChange={(v) => setForm({ ...form, pagoProximoValor: v.value })}
                        thousandSeparator="," decimalSeparator="." decimalScale={2} fixedDecimalScale
                        allowNegative={false} inputMode="decimal"
                      />
                    </CInputGroup>
                  </CCol>
                  <CCol md={4}>
                    <CFormLabel>Tipo</CFormLabel>
                    {/* [RGM] 2026-09-16 (pedido Rafael): Tipo con SELECT del
                        catálogo GX (subirpagosistema.cs:1022-1025). */}
                    <CFormSelect value={form.pagoTipo} onChange={(e) => setForm({ ...form, pagoTipo: e.target.value })}>
                      <option value="">Sin tipo</option>
                      {TIPOS_PAGO.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                    </CFormSelect>
                  </CCol>
                  <CCol md={4}>
                    <CFormLabel>Confirmado</CFormLabel>
                    {/* [RGM] 2026-09-16 (pedido Rafael): Confirmado con
                        SELECT del combo GX (pago.cs:3973-3974: 1=Si, 2=No). */}
                    <CFormSelect value={form.pagoConfirmado} onChange={(e) => setForm({ ...form, pagoConfirmado: e.target.value })}>
                      <option value="">Sin definir</option>
                      <option value="1">Si</option>
                      <option value="2">No</option>
                    </CFormSelect>
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>

            <CCard className="mb-3">
              <CCardHeader className="fw-semibold">Fechas</CCardHeader>
              <CCardBody>
                <CRow className="g-3">
                  <CCol md={4}><CFormLabel>Fecha</CFormLabel><CFormInput type="date" value={form.fecha} disabled={editando !== null} onChange={(e) => setForm({ ...form, fecha: e.target.value })} /></CCol>
                  <CCol md={4}><CFormLabel>Fecha Pago</CFormLabel><CFormInput type="date" value={form.pagoFechaPago} onChange={(e) => setForm({ ...form, pagoFechaPago: e.target.value })} /></CCol>
                  {/* [RGM] 2026-09-29 (pedido Rafael): los MÓDULOS (3/4/5/6) no
                      muestran próximo cobro; solo Registro (1) y Mantenimiento
                      (2) lo mueven. Para esos: arriba la fecha de la EMPRESA
                      (solo lectura, EmpresaProximoCobro) y abajo la del PAGO
                      (editable, la que al confirmar se asigna a la empresa). */}
                  {!['3', '4', '5', '6'].includes(form.pagoTipo) && (
                    <>
                      <CCol md={4}>
                        <CFormLabel>Próximo Cobro Empresa</CFormLabel>
                        <CFormInput disabled readOnly
                          value={String(editando?.empresaProximoCobro ?? '').slice(0, 10)} />
                      </CCol>
                      <CCol md={4}>
                        <CFormLabel>Próximo Cobro (Pago)</CFormLabel>
                        <CFormInput type="date" value={form.proximoCobro} onChange={(e) => setForm({ ...form, proximoCobro: e.target.value })} />
                      </CCol>
                    </>
                  )}
                </CRow>
              </CCardBody>
            </CCard>

            <CCard className="mb-3">
              <CCardHeader className="fw-semibold">Origen del pago</CCardHeader>
              <CCardBody>
                <CRow className="g-3">
                  <CCol md={4}>
                    <CFormLabel>Entidad</CFormLabel>
                    {/* [RGM] 2026-09-16 (pedido Rafael): Entidad con SELECT del
                        catálogo GX (subirpagosistema.cs:1033-1046). */}
                    <CFormSelect value={form.entidad} onChange={(e) => setForm({ ...form, entidad: e.target.value })}>
                      {ENTIDADES_PAGO.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                    </CFormSelect>
                  </CCol>
                  <CCol md={4}><CFormLabel>Remitente</CFormLabel><CFormInput value={form.remitente} onChange={(e) => setForm({ ...form, remitente: e.target.value })} onBlur={(e) => setForm({ ...form, remitente: capitalizarPalabras(e.target.value) })} /></CCol>
                  <CCol md={4}><CFormLabel>Refrencia</CFormLabel><CFormInput value={form.pagoRefrencia} onChange={(e) => setForm({ ...form, pagoRefrencia: e.target.value })} /></CCol>
                  <CCol md={4}><CFormLabel>Ciudad</CFormLabel><CFormInput value={form.pagoCiudad} onChange={(e) => setForm({ ...form, pagoCiudad: e.target.value })} onBlur={(e) => setForm({ ...form, pagoCiudad: capitalizarPalabras(e.target.value) })} /></CCol>
                  <CCol md={4}><CFormLabel>País</CFormLabel><CFormInput value={form.pagoPais} onChange={(e) => setForm({ ...form, pagoPais: e.target.value })} onBlur={(e) => setForm({ ...form, pagoPais: capitalizarPalabras(e.target.value) })} /></CCol>
                </CRow>
                {editando && (
                  <div className="mt-3">
                    <CFormLabel>Imagen del comprobante</CFormLabel>
                    {editando.comprobanteUrl ? (
                      <div>
                        <a href={editando.comprobanteUrl} target="_blank" rel="noreferrer">
                          <img src={editando.comprobanteUrl} alt="Comprobante"
                            style={{ maxWidth: '100%', maxHeight: 340, borderRadius: 8, border: '1px solid var(--cui-border-color)' }} />
                        </a>
                      </div>
                    ) : (
                      <div className="text-body-secondary small">Sin comprobante adjunto.</div>
                    )}
                  </div>
                )}
              </CCardBody>
            </CCard>

            <CCard className="mb-3">
              <CCardHeader className="fw-semibold">Observación</CCardHeader>
              <CCardBody>
                {/* [RGM] 2026-09-16 (pedido Rafael): se quitan los campos libres
                    del formulario — los valores existentes se conservan (el
                    payload los sigue enviando desde el estado del form). */}
                <CRow className="g-3">
                  <CCol md={12}><CFormLabel>Observación</CFormLabel><CFormInput value={form.observacion} onChange={(e) => setForm({ ...form, observacion: e.target.value })} onBlur={(e) => setForm({ ...form, observacion: capitalizarPalabras(e.target.value) })} /></CCol>
                </CRow>
              </CCardBody>
            </CCard>
          </CForm>
        </CModalBody>
        <CModalFooter className="border-top">
          <CButton color="secondary" type="button" disabled={guardando} onClick={() => { setAbierto(false); setEditando(null) }}>Cancelar</CButton>
          <CButton color="primary" type="button" disabled={guardando} onClick={() => formRef.current?.requestSubmit()}>{guardando ? <CSpinner size="sm" /> : editando ? 'Guardar cambios' : 'Guardar'}</CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default PagosPage
